import { useState } from 'react'
import { formatTRY } from '../../utils/money'
import type { Order, Table } from '../../types'
import { useCatalog } from '../../context/CatalogContext'
import { tableLabel, displayTable } from '../../utils/tableLabel'

interface Props {
  tables: Table[]
  /** Every unpaid ticket: the source list is these, and a busy target merges. */
  openTickets: Order[]
  onMove: (ticket: Order, tableName: string) => void
  onMerge: (source: Order, target: Order) => void
  onCancel: () => void
}

/**
 * Moves an open ticket from one table to another, picked as source then
 * destination. Customers change seats and two tables get pushed together for a
 * big group; without this the only remedy was voiding the ticket and retyping
 * every line.
 *
 * Reached from Ayarlar rather than from each ticket card: it is an occasional
 * correction, and an icon on every card was three buttons deep on a phone.
 */
export function MoveTicketDialog({ tables, openTickets, onMove, onMerge, onCancel }: Props) {
  const { halls } = useCatalog()
  const [source, setSource] = useState<Order | null>(null)
  /** The chosen destination when it already has a ticket, pending confirmation. */
  const [merging, setMerging] = useState<Order | null>(null)

  const ticketByTable = new Map(
    openTickets
      .filter((t) => t.tableName && t.id !== source?.id)
      .map((t) => [t.tableName as string, t]),
  )

  if (openTickets.length === 0) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg__card">
          <h2>Adisyon Taşı</h2>
          <p className="dlg__empty">Şu anda açık adisyon yok.</p>
          <div className="dlg__buttons">
            <button type="button" className="btn btn--ghost" onClick={onCancel}>
              Kapat
            </button>
          </div>
        </div>
      </div>
    )
  }

  // A merge changes two bills at once and closes one of them, so it is never a
  // single tap away.
  if (merging && source) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg__card">
          <h2>Adisyonları Birleştir</h2>

          <div className="movepick__flow">
            <span className="movepick__from">
              {displayTable(source.tableName)}
              <small>{formatTRY(source.totalKurus)}</small>
            </span>
            <span className="movepick__arrow">→</span>
            <span className="movepick__to">
              {displayTable(merging.tableName)}
              <small>{formatTRY(merging.totalKurus)}</small>
            </span>
          </div>

          <div className="dlg__total">
            <span>{displayTable(merging.tableName)} yeni toplam</span>
            <strong>{formatTRY(source.totalKurus + merging.totalKurus)}</strong>
          </div>

          <p className="dlg__warn">
            {displayTable(source.tableName)} adisyonu kapanır. Bu işlem geri alınamaz.
          </p>

          <div className="dlg__buttons">
            <button type="button" className="btn btn--ghost" onClick={() => setMerging(null)}>
              Geri
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => onMerge(source, merging)}
            >
              Birleştir
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Step 2: where it goes.
  if (source) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg__card dlg__card--wide">
          <h2>Nereye taşınacak?</h2>

          {/* Table above, amount below: on a phone the two ran onto separate
              lines mid-sentence and read as one broken value. */}
          <div className="movepick__source">
            <span className="movepick__source-label">Taşınan adisyon</span>
            <strong className="movepick__source-name">{displayTable(source.tableName)}</strong>
            <span className="movepick__source-total">{formatTRY(source.totalKurus)}</span>
          </div>

          {ticketByTable.size > 0 && (
            <p className="dlg__hint">
              Sarı masalarda açık adisyon var. Seçerseniz iki adisyon birleşir.
            </p>
          )}

          <div className="tablepick">
            {tables
              .filter((t) => tableLabel(t, halls) !== source.tableName)
              .map((t) => {
                const label = tableLabel(t, halls)
                const busy = ticketByTable.get(label)
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={busy ? 'is-busy' : ''}
                    onClick={() => (busy ? setMerging(busy) : onMove(source, label))}
                  >
                    {displayTable(label)}
                    {busy && (
                      <span className="tablepick__busy">{formatTRY(busy.totalKurus)}</span>
                    )}
                  </button>
                )
              })}
          </div>

          <div className="dlg__buttons">
            <button type="button" className="btn btn--ghost" onClick={() => setSource(null)}>
              Geri
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Step 1: which ticket is moving.
  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card dlg__card--wide">
        <h2>Hangi adisyon taşınacak?</h2>

        <div className="movepick">
          {openTickets.map((t) => (
            <button key={t.id} type="button" onClick={() => setSource(t)}>
              <span className="movepick__name">{displayTable(t.tableName) ?? 'Masa yok'}</span>
              <span className="movepick__meta">
                {t.itemCount} ürün
                {t.openedByName && `, ${t.openedByName}`}
              </span>
              <span className="movepick__total">{formatTRY(t.totalKurus)}</span>
            </button>
          ))}
        </div>

        <div className="dlg__buttons">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  )
}
